import { useState, useEffect } from 'react';

export default function Hero() {
  const [messages, setMessages] = useState([
    { text: "Hi! I'm your AI assistant. What type of vehicle are you looking for?", isBot: true, show: true },
  ]);
  const [showTyping, setShowTyping] = useState(false);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    timers.push(setTimeout(() => {
      setShowTyping(true);
    }, 2000));

    timers.push(setTimeout(() => {
      setShowTyping(false);
      setMessages(prev => [...prev, { text: "I'm looking for an SUV, preferably a Toyota RAV4", isBot: false, show: true }]);
    }, 3500));

    timers.push(setTimeout(() => {
      setShowTyping(true);
    }, 4000));

    timers.push(setTimeout(() => {
      setShowTyping(false);
      setMessages(prev => [...prev, { text: "Great choice! What's your budget range?", isBot: true, show: true }]);
    }, 5500));

    return () => timers.forEach(timer => clearTimeout(timer));
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background with image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/5fb4188dc1db6e50d6c3e79cbe3ab0af/6ed9b5f26b32078114249099bd363a13.jpeg"
          alt="AI-Powered Auto Broker Woodbridge Ontario"
          title="Auto Broker Woodbridge - AI Car Buying Service"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <header className="space-y-6">
            <div className="inline-block">
              <div className="bg-purple-600/20 backdrop-blur-sm border border-purple-400/30 text-purple-300 px-4 py-2 rounded-full text-sm font-semibold animate-pulse">
                ✨ AI-ASSISTED BUYING
              </div>
            </div>

            <div className="space-y-2">
              <h2 className="text-6xl md:text-7xl font-bold text-white leading-tight">
                NEVER BUY A CAR
              </h2>
              <div className="text-5xl md:text-6xl font-semibold text-gray-300 leading-tight">
                WITHOUT TALKING TO
              </div>
              <div className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent leading-tight">
                AN <strong>AI-POWERED BROKER</strong>
              </div>
            </div>

            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Save <strong>40+ Hours &amp; $1000s</strong> | <strong>Woodbridge, Ontario's</strong> First AI Auto Broker
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <button
                onClick={scrollToContact}
                className="bg-gradient-to-r from-cyan-500 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:shadow-2xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap flex items-center space-x-2"
              >
                <span>START YOUR AI CONSULTATION</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>

            <div className="flex items-center space-x-2 text-gray-400 text-sm pt-2">
              <i className="ri-lock-line"></i>
              <span>Your data is secure | 2-minute setup</span>
            </div>
          </header>

          {/* Right Interactive Zone - AI Chat Preview */}
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
              <div className="space-y-4 mb-6 min-h-[300px]">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.isBot ? 'justify-start' : 'justify-end'} animate-fade-in`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                        msg.isBot
                          ? 'bg-gradient-to-r from-purple-600 to-cyan-600 text-white'
                          : 'bg-white text-gray-900'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}
                
                {showTyping && (
                  <div className="flex justify-start">
                    <div className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white px-4 py-3 rounded-2xl">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={scrollToContact}
                className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 text-white py-4 rounded-xl text-lg font-bold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center space-x-2"
              >
                <span>START YOUR AI CONSULTATION</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </button>

              <div className="text-center text-white/70 text-sm mt-4">
                🔒 Your data is secure | 2-minute setup
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating particles effect */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-50"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s infinite ease-in-out`,
              animationDelay: `${Math.random() * 5}s`
            }}
          ></div>
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </section>
  );
}
